import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import TradeForm from './TradeForm'
import TradesList from './TradesList'
import * as FiIcons from 'react-icons/fi'

const { FiPlus, FiFilter, FiSearch, FiAlertCircle, FiCheckCircle } = FiIcons

const Journal = () => {
  const { user } = useAuth()
  const location = useLocation()
  const [trades, setTrades] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingTrade, setEditingTrade] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterBy, setFilterBy] = useState('all')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (user) {
      loadTrades()
    }
    if (location.state?.openForm) {
      setShowForm(true)
      window.history.replaceState({}, document.title)
    }
  }, [user, location.state])

  const loadTrades = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getUserTrades(user.id, 100)
      setTrades(tradesData)
    } catch (error) {
      console.error('Error loading trades:', error)
      setError('Failed to load trades. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  const handleTradeSubmit = async (tradeData) => {
    try {
      setError('')
      setSuccess('')
      if (editingTrade) {
        await dbHelpers.updateTrade(editingTrade.id, tradeData)
        setSuccess('Trade updated successfully!')
      } else {
        await dbHelpers.addTrade({ ...tradeData, user_id: user.id })
        setSuccess('New trade added to your journal!')
      }
      await loadTrades()
      setShowForm(false)
      setEditingTrade(null)
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error saving trade:', error)
      setError(error.message || 'Failed to save trade. Please check your inputs.')
    }
  }

  const handleDeleteTrade = async (tradeId) => {
    try {
      setError('')
      await dbHelpers.deleteTrade(tradeId)
      setTrades(prev => prev.filter(t => t.id !== tradeId))
      setSuccess('Trade deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Delete error:', error)
      setError('Failed to delete trade. Please try again.')
    }
  }

  const handleEditTrade = (trade) => {
    setEditingTrade(trade)
    setShowForm(true)
  }

  const filteredTrades = trades.filter(trade => {
    const symbolMatch = trade.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
    const setupMatch = trade.setup_type?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSearch = symbolMatch || setupMatch
    
    const matchesFilter = 
      filterBy === 'all' || 
      (filterBy === 'wins' && (trade.pnl || 0) > 0) || 
      (filterBy === 'losses' && (trade.pnl || 0) < 0) ||
      (filterBy === 'buys' && trade.side === 'buy') ||
      (filterBy === 'sells' && trade.side === 'sell')
      
    return matchesSearch && matchesFilter
  })

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trading Journal</h1>
          <p className="text-gray-600 mt-1">Record and track your trading activities in real-time</p>
        </div>
        <button 
          onClick={() => { setError(''); setShowForm(true); }} 
          className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2.5 rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-200 active:scale-95"
        >
          <SafeIcon icon={FiPlus} className="h-5 w-5" />
          <span className="font-semibold">Add Trade</span>
        </button>
      </motion.div>

      {/* Notifications */}
      <div className="space-y-4">
        {error && (
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-2 text-red-700 shadow-sm">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </motion.div>
        )}
        {success && (
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-2 text-green-700 shadow-sm">
            <SafeIcon icon={FiCheckCircle} className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">{success}</p>
          </motion.div>
        )}
      </div>

      {/* Filters & Search */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.1 }} 
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
            </div>
            <input 
              type="text" 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              placeholder="Search by symbol (e.g. BTC) or setup..." 
              className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none" 
            />
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
              <SafeIcon icon={FiFilter} className="h-4 w-4 text-gray-400" />
              <select 
                value={filterBy} 
                onChange={(e) => setFilterBy(e.target.value)} 
                className="bg-transparent border-none text-sm font-medium focus:ring-0 outline-none cursor-pointer"
              >
                <option value="all">All Trades</option>
                <option value="wins">Winning Trades</option>
                <option value="losses">Losing Trades</option>
                <option value="buys">Long Positions</option>
                <option value="sells">Short Positions</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Trade Form Modal */}
      {showForm && (
        <TradeForm 
          trade={editingTrade} 
          onSubmit={handleTradeSubmit} 
          onClose={() => { setShowForm(false); setEditingTrade(null); }} 
        />
      )}

      {/* Trades List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }} 
      >
        <TradesList 
          trades={filteredTrades} 
          loading={loading} 
          onEditTrade={handleEditTrade}
          onDeleteTrade={handleDeleteTrade}
        />
      </motion.div>
    </div>
  )
}

export default Journal